import type { QueryClient, QueryKey } from "@tanstack/react-query";
import type { ServerInferRequest, ServerInferResponses } from "@ts-rest/core";
import { taskContract } from "@common/contracts/task.contract";
import {
    isTaskTerminatingStatus,
    type Task,
    TaskSchema,
} from "@common/types/task";
import { organizationTaskListsFilter } from "./organizationTask.api";
import { taskClient } from "./taskClient";
import { invalidateClientsCloseOverviewQueries } from "./workspace.api";
import { invalidateTodoTaskCounts, tasksQueryKeys } from "./taskQueryKeys";

export type TaskUpdateBody = ServerInferRequest<
    typeof taskContract.updateTask
>["body"];

/** Fields the server sets on an update, which the cache shows ahead of the response. */
export type OptimisticTaskFields = Pick<
    Partial<Task>,
    "assignee" | "updatedAt" | "lastDoneAt" | "lastDoneById" | "lastDoneBy"
>;

/** Sends a task update to the task's own workspace. */
export async function requestTaskUpdate({
    task,
    body,
}: {
    task: Task;
    body: TaskUpdateBody;
}) {
    const res = await taskClient.updateTask({
        params: { workspaceId: task.workspaceId, taskId: task.id },
        body,
    });

    return TaskSchema.parse(res.body);
}

interface TaskUpdateCacheArgs {
    queryClient: QueryClient;
    /** The task's workspace key: lists on both Tasks pages are cached under it. */
    workspaceKey: string;
    taskId: string;
}

/**
 * Stops in-flight list and detail refetches so they can't clobber an optimistic
 * write. The organization Tasks page lists every client's tasks in one cached
 * list, so those lists are covered too.
 */
export async function cancelTaskUpdateQueries({
    queryClient,
    workspaceKey,
    taskId,
}: TaskUpdateCacheArgs) {
    await queryClient.cancelQueries({
        queryKey: tasksQueryKeys.listByWorkspaceKey(workspaceKey),
        exact: false,
    });
    await queryClient.cancelQueries(organizationTaskListsFilter);
    await queryClient.cancelQueries({
        queryKey: tasksQueryKeys.detail(workspaceKey, taskId),
        exact: false,
    });
}

/** The cached lists and detail from before an optimistic task update. */
export interface TaskUpdateSnapshot {
    previousLists: Array<[QueryKey, Task[] | undefined]>;
    previousDetail: Task | undefined;
    detailKey: QueryKey;
}

/**
 * Writes `payload` onto the task in every cached list and its detail right
 * away, and adjusts the blocker counts of the tasks it blocks when it enters
 * or leaves Done or Canceled. Returns the snapshot to roll back to.
 */
export function writeOptimisticTaskUpdate({
    queryClient,
    workspaceKey,
    task,
    payload,
}: Omit<TaskUpdateCacheArgs, "taskId"> & {
    task: Task;
    payload: TaskUpdateBody & OptimisticTaskFields;
}): TaskUpdateSnapshot {
    const listKey = tasksQueryKeys.listByWorkspaceKey(workspaceKey);
    const detailKey = tasksQueryKeys.detail(workspaceKey, task.id);

    const previousLists = [
        ...queryClient.getQueriesData<Task[]>({
            queryKey: listKey,
            exact: false,
        }),
        ...queryClient.getQueriesData<Task[]>(organizationTaskListsFilter),
    ];
    const previousDetail = queryClient.getQueryData<Task>(detailKey);

    const statusPayload = payload.status;
    const isBlockerCountAffected =
        statusPayload !== undefined &&
        isTaskTerminatingStatus(statusPayload) !==
            isTaskTerminatingStatus(task.status);

    let delta = 0;
    if (isBlockerCountAffected) {
        delta = isTaskTerminatingStatus(statusPayload) ? -1 : 1;
    }

    const projectFinancialDocuments = (
        current: Task["financialDocuments"],
    ): Task["financialDocuments"] => {
        if (
            payload.financialDocumentIds === undefined ||
            current === undefined
        ) {
            return current;
        }
        const idSet = new Set(payload.financialDocumentIds);
        // We can only reliably project removals; new IDs need the
        // server's hydrated `TaskFinancialDocument` shape, so the
        // section UI catches up on refetch. The picker reads the
        // ID set directly from `financialDocumentIds`, so it
        // reflects attachments immediately.
        return current.filter((doc) => idSet.has(doc.id));
    };

    const writeTaskIntoList = (oldData: Task[] | undefined) => {
        if (!oldData) {
            return oldData;
        }
        return oldData.map((t) => {
            if (t.id === task.id) {
                return {
                    ...t,
                    ...payload,
                    financialDocuments: projectFinancialDocuments(
                        t.financialDocuments,
                    ),
                };
            }
            if (delta !== 0 && t.blockedByTaskIds?.includes(task.id)) {
                return {
                    ...t,
                    numberOfActiveBlockedByTasks: Math.max(
                        0,
                        (t.numberOfActiveBlockedByTasks ?? 0) + delta,
                    ),
                };
            }
            return t;
        });
    };
    queryClient.setQueriesData(
        { queryKey: listKey, exact: false },
        writeTaskIntoList,
    );
    queryClient.setQueriesData(organizationTaskListsFilter, writeTaskIntoList);

    queryClient.setQueryData(detailKey, (oldData: Task | undefined) => {
        if (!oldData) {
            return oldData;
        }
        return {
            ...oldData,
            ...payload,
            financialDocuments: projectFinancialDocuments(
                oldData.financialDocuments,
            ),
        };
    });

    return { previousLists, previousDetail, detailKey };
}

/** Puts the cached lists and detail back as `writeOptimisticTaskUpdate` found them. */
export function restoreTaskUpdateSnapshot(
    queryClient: QueryClient,
    snapshot: TaskUpdateSnapshot,
) {
    for (const [key, data] of snapshot.previousLists) {
        queryClient.setQueryData(key, data);
    }
    queryClient.setQueryData(snapshot.detailKey, snapshot.previousDetail);
}

/**
 * Puts only the snapshot's task back as it was, leaving the rest of each
 * cached list as it is now: for one failure among several updates written
 * after it, which a whole-list restore would undo too.
 */
export function restoreTaskFromUpdateSnapshot({
    queryClient,
    snapshot,
    taskId,
}: {
    queryClient: QueryClient;
    snapshot: TaskUpdateSnapshot;
    taskId: string;
}) {
    for (const [key, previousTasks] of snapshot.previousLists) {
        const previousTask = previousTasks?.find((task) => task.id === taskId);
        if (previousTask === undefined) {
            continue;
        }
        queryClient.setQueryData<Task[]>(key, (tasks) =>
            tasks?.map((task) => (task.id === taskId ? previousTask : task)),
        );
    }
    queryClient.setQueryData(snapshot.detailKey, snapshot.previousDetail);
}

/** Refetches what a task update can change: lists, the detail, summaries and To do counts. */
export function invalidateUpdatedTaskQueries({
    queryClient,
    workspaceKey,
    taskId,
}: TaskUpdateCacheArgs) {
    queryClient.invalidateQueries({
        queryKey: tasksQueryKeys.listByWorkspaceKey(workspaceKey),
        exact: false,
    });
    queryClient.invalidateQueries({
        queryKey: tasksQueryKeys.detail(workspaceKey, taskId),
        exact: false,
    });
    queryClient.invalidateQueries({
        queryKey: tasksQueryKeys.summaries(workspaceKey),
        exact: true,
    });
    invalidateTodoTaskCounts(queryClient, workspaceKey);
}

/**
 * Work page health reads a close task's due date and stage. Changing those
 * alone doesn't publish the close stage event that refreshes the Work page.
 */
export function invalidateCloseHealthQueries({
    queryClient,
    task,
    body,
}: {
    queryClient: QueryClient;
    task: Task;
    body: TaskUpdateBody;
}) {
    const isCloseTask = Boolean(task.closeId ?? body.closeId);
    const hasHealthChange =
        body.dueDate !== undefined ||
        body.closeStageId !== undefined ||
        body.closeId !== undefined;
    if (isCloseTask && hasHealthChange) {
        invalidateClientsCloseOverviewQueries(queryClient);
    }
}

/** The client types only 200, but the server answers a larger bulk update with 202. */
export type TaskBulkUpdateResponse = ServerInferResponses<
    typeof taskContract.bulkUpdateTasks,
    200 | 202
>;

/**
 * Whether a bulk update was applied before it answered. A larger one runs as
 * a job (202 async), whose progress events patch the cache instead, so a
 * refetch right away would only bring back the old values.
 */
export const isTaskBulkUpdateApplied = (response: TaskBulkUpdateResponse) =>
    response.status === 200 && response.body.mode === "sync";
